import { firestoreInstance } from 'config/ApiService';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { HttpsCallableResult } from 'firebase/functions';
import PhioengageService, { ServiceResponse } from 'services/PhioengageService';
import { DischargeSettingsType } from '../components/DischargeSettingsForm';
import { Dayjs } from 'dayjs';

export const getSymptomLogs = async (
    userId: string,
    region: string,
    fromDate?: Dayjs | null,
    toDate?: Dayjs | null
): Promise<void | HttpsCallableResult<ServiceResponse>> => {
    const service = new PhioengageService<
        { user_id: string; region: string; from_date?: string | null; to_date?: string | null },
        ServiceResponse
    >();
    const resp = await service.invoke(
        {
            user_id: userId,
            region,
            from_date: fromDate?.toISOString() ?? null,
            to_date: toDate?.toISOString() ?? null
        },
        'phioengage-symptom-getlogs'
    );
    return resp;
};

export const updateTaskOwnerAssignment = async (param: {
    userId: string;
    region: string;
    operation: 'assign' | 'remove';
}): Promise<void | HttpsCallableResult<ServiceResponse>> => {
    const service = new PhioengageService<any, ServiceResponse>();
    const resp = await service.invoke(
        {
            user_id: param.userId,
            region: param.region,
            operation: param.operation
        },
        'phioengage-profile-assignclinician'
    );
    return resp;
};

export const hasPendingTasks = async (userId: string, programmeId: string): Promise<boolean> => {
    const ref = collection(
        firestoreInstance(),
        `phioengage_user_profiles/${userId}/scheduled_tasks`
    );
    const querySnapshot = await getDocs(
        query(
            ref,
            where('archived', '==', false),
            where('reviewed', '==', false),
            where('programme_id', '==', programmeId),
            where('review_date', '<=', new Date())
        )
    );
    if (querySnapshot.empty) {
        return false;
    }
    return querySnapshot.docs.length > 0;
};

export const hasOpenThreads = async (userId: string): Promise<boolean> => {
    const ref = collection(
        firestoreInstance(),
        `phioengage_user_profiles/${userId}/direct_message_threads`
    );
    const querySnapshot = await getDocs(query(ref, where('status', '==', 'open')));
    return !querySnapshot.empty;
};

export const saveCustomerDischargeConfig = async (
    customerId: string,
    dischargeCode: string,
    config: DischargeSettingsType,
    region: string
) => {
    const service = new PhioengageService<any, ServiceResponse>();
    const {
        appOverlayHeader,
        appOverlayMessage,
        dischargeAfterInDays,
        customerNote,
        treatmentNote,
        patientEmailBody,
        patientEmailSubject
    } = config;
    const resp = await service.invoke(
        {
            customer_id: customerId,
            discharge_code: dischargeCode,
            app_overlay_header: appOverlayHeader,
            app_overlay_message: appOverlayMessage,
            discharge_after_in_days:
                parseInt(String(dischargeAfterInDays), 10) > 0
                    ? parseInt(String(dischargeAfterInDays), 10)
                    : null,
            customer_note: customerNote,
            treatment_note: treatmentNote,
            patient_email_subject: patientEmailSubject,
            patient_email_body: patientEmailBody,
            region
        },
        'phioengage-customer-savedischargeconfig'
    );
    return resp;
};

/**
 * Marks the last message from a patient as read.
 * This is typically used when a support agent has read the last message from a patient in a chat thread.
 * @param {string} userId
 * @param {string} lastMessageId
 * @param {string} region
 * @param {string} requestor
 * @throws {Error} If the service invocation fails.
 * @returns {Promise<void>} A promise that resolves when the operation is complete.
 */
export const markLastMessageFromPatientAsRead = async (
    userId: string,
    lastMessageId: string,
    region: string,
    requestor: 'support'
): Promise<void> => {
    const service = new PhioengageService<any, { success: boolean }>();
    await service.invoke(
        {
            user_id: userId,
            last_message_id: lastMessageId,
            other_participant_id: requestor,
            region
        },
        'phioengage-chat-markasread'
    );
};

/**
 * @param {string} userId - The ID of the user whose programme is to be reset.
 * @param {string} region - The region of the user.
 * @remarks This function resets the user's programme by invoking a Phioengage service.
 * It is typically used when a user needs to start over with their programme.
 * @throws {Error} If the service invocation fails.
 * @returns
 */
export const resetPatientProgramme = async (userId: string, region: string): Promise<unknown> => {
    const service = new PhioengageService<any, { success: boolean }>();
    const resp = await service.invoke(
        {
            user_id: userId,
            region
        },
        'phioengage-casework-reset'
    );
    return resp;
};

/**
 *
 * @param {string} userId - The ID of the user whose programme is to be changed.
 * @param {string} region - The region of the user.
 * @param {string} programmeId - The ID of the new programme to be assigned to the user.
 * @remarks This function changes the user's programme by invoking a Phioengage service.
 * It is typically used when a user needs to switch to a different programme.
 * @throws {Error} If the service invocation fails.
 * @returns
 */
export const changePatientProgramme = async (
    userId: string,
    region: string,
    programmeId: string
): Promise<unknown> => {
    const service = new PhioengageService<any, { success: boolean }>();
    const resp = await service.invoke(
        {
            user_id: userId,
            region,
            new_programme_id: programmeId
        },
        'phioengage-profile-handleprogrammechange'
    );
    return resp;
};
